import { Button, Grid } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import FormDialog from "../../component/common/FormDialog";
import RouteDetail from "../../component/route/RouteDetail";
import { useDispatch, useSelector } from "react-redux";
import { IRootState, IRootThunkDispatch } from "../../redux/store";
import {
    OpenSnackBarWithResultFail,
    OpenSnackBarWithResultSuccess,
} from "../../redux/common/snackBar/thunk";
import {
    axiosAPIWithJson,
    axiosGet,
    axiosPostWithFormData,
} from "../../util/axios";
import { OpenLoadingBarThunk } from "../../redux/common/isLoading/thunk";
import {
    ClearRouteFormDataThunk,
    GetDataFromRouteListThunk,
    initRouteListThunk,
} from "../../redux/route/thunk";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { EditNote } from "@mui/icons-material";
import { RouteState } from "../../redux/route/state";
import RouteExcel from "../../component/route/RouteExcel";
import { ButtonTextGrid } from "../..";

function RouteName() {
    const dispatch = useDispatch<IRootThunkDispatch>();
    const routeState = useSelector((state: IRootState) => state.routeState);
    const { routeList } = routeState;
    const dataFetchedRef = useRef(false);
    const [openRouteDialog, setOpenRouteDialog] = useState<boolean>(false);
    const [openEditRouteDialog, setOpenEditRouteDialog] =
        useState<boolean>(false);
    const [openExcelRouteDialog, setOpenExcelRouteDialog] =
        useState<boolean>(false);
    const [passCheck, setPassCheck] = useState<boolean>(false);
    const [excelFile, setExcelFile] = useState<File[] | null>(null);
    const [passCheckMessage, setPassCheckMessage] = useState<string>("");
    const [failedRowList, setFailedRowList] = useState<Record<string, any>>({});
    const [rowListCount, setRowListCount] = useState<Record<string, any>>({});
    const [rowLength, setRowLength] = useState<number>(0);
    const {
        collector_id,
        selectedBinInfo,
        route_name,
        weekday,
        remark,
        effective_day,
        route_id,
    } = routeState;

    useEffect(() => {
        const controller = new AbortController();
        if (dataFetchedRef.current) return;
        dataFetchedRef.current = true;
        getRouteData();
        return () => {
            controller.abort();
        };
    }, []);

    async function getRouteData() {
        dispatch(OpenLoadingBarThunk(true));
        const result = await axiosGet("/api/routes/cms");
        dispatch(OpenLoadingBarThunk(false));
        if (result.data.success) {
            dispatch(initRouteListThunk(result.data.routeList));
        } else {
            dispatch(OpenSnackBarWithResultFail("未能取得查詢資料"));
        }
    }

    function handleAddRoute() {
        dispatch(ClearRouteFormDataThunk());
        setOpenRouteDialog(true);
    }
    function handleExcelRoute() {
        dispatch(ClearRouteFormDataThunk());
        setOpenExcelRouteDialog(true);
    }

    function handleCloseDialog() {
        dispatch(ClearRouteFormDataThunk());
        setOpenRouteDialog(false);
    }

    interface WithHolidayFlag {
        holiday?: number | boolean | null;
        is_holiday_suitable?: number | boolean | null;
        [key: string]: unknown;
    }

    function isHolidaySuitable(route: WithHolidayFlag | null | undefined): boolean {
        if (!route) return false;
        return (
            route.holiday === 1 || route.holiday === true ||
            route.is_holiday_suitable === 1 || route.is_holiday_suitable === true
        );
    }

    async function handleSubmitRoute() {
        let result;
        if (!route_name) {
            dispatch(OpenSnackBarWithResultFail("你未有填寫車線名稱"));
            return;
        }
        if (!collector_id) {
            dispatch(OpenSnackBarWithResultFail("你未有選擇收集者"));
            return;
        }

        if (!selectedBinInfo) {
            dispatch(OpenSnackBarWithResultFail("你未有選擇桶名稱"));
            return;
        }
        if (!weekday) {
            dispatch(OpenSnackBarWithResultFail("你未有選擇工作日"));
            return;
        }

        const sameCollectorRoutes =
            routeList?.filter((route) => {
                if (!route) {
                    return false;
                }
                if (!collector_id) {
                    return false;
                }
                if (route.collector_id !== collector_id) {
                    return false;
                }
                if (route.route_id && route_id && route.route_id === route_id) {
                    return false;
                }
                return true;
            }) || [];

        const hasWeekdayConflict = sameCollectorRoutes.some((route) => {
            return String(route.weekday) === String(weekday);
        });

        if (hasWeekdayConflict) {
            dispatch(
                OpenSnackBarWithResultFail(
                    "此司機在該工作日已存在車線，不能重複設定"
                )
            );
            return;
        }

        const currentRouteHolidaySuitable = isHolidaySuitable(routeState);

        if (currentRouteHolidaySuitable) {
            const hasHolidayConflict = sameCollectorRoutes.some((route) =>
                isHolidaySuitable(route)
            );

            if (hasHolidayConflict) {
                dispatch(
                    OpenSnackBarWithResultFail(
                        "此司機已有一條假日適用車線，不能再新增／設定另一條"
                    )
                );
                return;
            }
        }

        const form = {
            route_name,
            collector_id,
            selectedBinInfo,
            remark,
            effective_day,
            weekday,
        };

        dispatch(OpenLoadingBarThunk(true));

        if (route_id) {
            result = await axiosAPIWithJson.put(`api/routes/${route_id}`, form);
        } else {
            result = await axiosAPIWithJson.post("api/routes", form);
        }
        if (result?.data.success) {
            dispatch(ClearRouteFormDataThunk());
            dispatch(OpenLoadingBarThunk(false));
            dispatch(OpenSnackBarWithResultSuccess(result.data.message));
            getRouteData();
            if (route_id) {
                handleCloseEditRouteDialog();
            } else {
                handleCloseDialog();
            }
        } else {
            dispatch(OpenLoadingBarThunk(false));
            dispatch(OpenSnackBarWithResultFail(result?.data.message));
            if (route_id) {
                handleCloseEditRouteDialog();
            } else {
                handleCloseDialog();
            }
        }
    }

    async function handleSubmitExcelRoute() {
        if (!excelFile) {
            dispatch(OpenSnackBarWithResultFail("你未有上傳excel"));
            return;
        }
        const formData = new FormData();
        formData.append("route_excel", excelFile[0]);
        let result;
        try {
            dispatch(OpenLoadingBarThunk(true));
            if (passCheck) {
                result = await axiosPostWithFormData(
                    "/api/routes/upload/collection",
                    formData
                );

                if (result.data.success) {
                    dispatch(
                        OpenSnackBarWithResultSuccess('你已成功上傳車線')
                        );
                    handleCloseExcelRouteDialog();
                } else {
                    dispatch(OpenSnackBarWithResultFail(result.data.message));
                }
                handleCloseExcelRouteDialog();
            } else {
                result = await axiosPostWithFormData(
                    "/api/routes/check",
                    formData
                );
                setPassCheckMessage(result.data.message);
                if (result.data.success) {
                    setPassCheck(true);
                    setRowListCount(result.data.data);
                    dispatch(
                        OpenSnackBarWithResultSuccess(result.data.message)
                    );
                } else {
                    dispatch(OpenSnackBarWithResultFail(result.data.message));
                    if (result.data.data) {
                        setFailedRowList(result.data.data);
                    }
                }
                if (result.data.length) {
                    setRowLength(result.data.length);
                }
                dispatch(OpenLoadingBarThunk(false));
            }
            dispatch(OpenLoadingBarThunk(false));
        } catch (err) {
            dispatch(OpenLoadingBarThunk(false));
            dispatch(OpenSnackBarWithResultFail("網路錯誤"));
        }
    }

    async function handleOpenEditRouteDialog(routeData: RouteState) {
        const result = await axiosGet(
            `/api/routes/cms/detail/${routeData.route_id}`
        );
        if (result.data.success) {
            dispatch(
                GetDataFromRouteListThunk(routeData, result.data.routeList)
            );
        }
        setOpenEditRouteDialog(true);
    }

    function handleCloseEditRouteDialog() {
        setOpenEditRouteDialog(false);
    }

    function handleCloseExcelRouteDialog() {
        setOpenExcelRouteDialog(false);
        setPassCheck(false);
        setFailedRowList({});
        setRowListCount({});
        setRowLength(0);
        setPassCheckMessage("");
    }

    const columns: GridColDef[] = [
        { field: "route_id", headerName: "路線編號", flex: 1 },
        { field: "route_name", headerName: "路線名稱", flex: 1 },
        {
            field: "username",
            headerName: "負責貨車",
            flex: 1,
        },
        { field: "effective_day", headerName: "生效時間", flex: 1 },
        { field: "create_date", headerName: "建立日期", flex: 1 },
        { field: "remark", headerName: "備註", flex: 1 },

        {
            field: "action",
            headerName: "詳情",
            flex: 1,
            renderCell: (param) => (
                <Button
                    variant="contained"
                    onClick={() => handleOpenEditRouteDialog(param.row)}
                >
                    <ButtonTextGrid>
                    <EditNote />
                    <Grid>詳情／更改</Grid>
                    </ButtonTextGrid>


                </Button>
            ),
        },
    ];

    // const currentDate = moment(); // Get the current date
    // const filterValue = currentDate.subtract(0, "months").format("YYYY-MM");

    return (
        <div>
            <Button variant="contained" onClick={handleAddRoute}>
                新增車線
            </Button>
            <Button
                variant="contained"
                onClick={handleExcelRoute}
                style={{ marginLeft: "10px" }}
            >
                上傳車線excel
            </Button>

            <div style={{ height: "900px", width: "100%" }}>
                <DataGrid
                    rows={routeList || []}
                    columns={columns}
                    initialState={{
                        pagination: {
                            paginationModel: { page: 0, pageSize: 30 },
                        },
                        // filter: {
                        //     filterModel: {
                        //         items: [
                        //             {
                        //                 field: "effective_day",
                        //                 operator: "contains",
                        //                 value: filterValue,
                        //             },
                        //         ],
                        //     },
                        // },
                    }}
                    pageSizeOptions={[5, 10]}
                    checkboxSelection
                    getRowId={(row) => row.route_id}
                />
            </div>

            <FormDialog
                title="增加車線"
                open={openRouteDialog}
                handleClose={handleCloseDialog}
                handleSubmit={handleSubmitRoute}
                size="lg"
            >
                <RouteDetail />
            </FormDialog>

            <FormDialog
                title="詳情／更改車線"
                open={openEditRouteDialog}
                handleClose={handleCloseEditRouteDialog}
                handleSubmit={handleSubmitRoute}
                size="lg"
            >
                <RouteDetail />
            </FormDialog>

            <FormDialog
                title="上傳車線excel"
                open={openExcelRouteDialog}
                handleClose={handleCloseExcelRouteDialog}
                handleSubmit={handleSubmitExcelRoute}
                buttonName={passCheck ? "上傳" : "檢查"}
                size="lg"
            >
                <RouteExcel
                    onChangeFunc={(file: File[]) => {
                        setExcelFile(file);
                        setPassCheck(false);
                        setFailedRowList({});
                        setPassCheckMessage("");
                        setRowLength(0);
                    }}
                    passCheckMessage={passCheckMessage}
                    failedRowList={failedRowList}
                    rowListCount={rowListCount}
                    rowLength={rowLength}
                />
            </FormDialog>
        </div>
    );
}

export default RouteName;
